"use client";

import { quoteThemeColorStyles, quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { useState, useEffect } from "react";
import { iCTABlockData } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { convertHtmlToPlainText } from "@/utils/html-to-plain-text";
import { SDK } from "@/@sdk";
import { useHolidayQuoteBrand } from "../contexts/use-holiday-quote-brand";
import { CheckCircle, Send, AlertCircle, Loader2 } from "lucide-react";
import { QuoteBookingConfirmedDepositReminder } from "./quote-booking-confirmed-deposit-reminder";
import { QuoteBookingConfirmationOptions } from "./quote-booking-confirmation-options";

interface CTASectionProps extends iCTABlockData {
  primaryColor?: string; // legacy, theme CSS vars used
}

export function CTASection({
  feedbackBody,
  bookingBody,
  depositAmount,
  depositDueDate,
    ...blockData
}: CTASectionProps) {
  const [activeTab, setActiveTab] = useState<"book" | "feedback">("book");
  const [feedback, setFeedback] = useState("");
  const [status, setStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bookingTitle, setBookingTitle] = useState(
    "Ready to Secure Your Holiday"
  );

  const { quote, isAdminMode } = useHolidayQuoteBrand();

  const alreadyBooked = quote.metadata?.status === "CONFIRMED";

  useEffect(() => {
    const timer = setTimeout(() => {
      setBookingTitle(
        convertHtmlToPlainText(bookingBody || "")
          ? bookingBody!
          : "Ready to Secure Your Holiday"
      );
    }, 1000);

    return () => clearTimeout(timer);
  }, [bookingBody]);

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: null, message: "" });
    setIsLoading(true);

    try {
      if (isAdminMode) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setStatus({
          type: "success",
          message: "Feedback submitted successfully (Admin Mode)",
        });
        return;
      }

      await SDK.holidayQuotesX.addFeedback(quote.id!, { comment: feedback });

      setStatus({
        type: "success",
        message:
          "Thank you for your feedback! We'll review your request and get back to you soon.",
      });
      setFeedback("");
    } catch (error) {
      setStatus({
        type: "error",
        message: "Unable to submit feedback. Please email us.",
      });
      console.error("Feedback error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (bookingConfirmed || alreadyBooked) {
    return (
      <div
        className="bg-white rounded-xl shadow-2xl border-2 p-4 sm:p-8 text-center space-y-4 sm:space-y-6 relative overflow-hidden hover:shadow-3xl transition-all duration-300"
        style={quoteThemeColorStyles.accentBorder}
      >
        <div
          className="absolute top-0 right-0 text-white px-6 py-1.5 rounded-bl-2xl text-xs font-semibold flex items-center gap-1"
          style={quoteThemeColorStyles.primaryBg}
        >
          <CheckCircle className="w-3 h-3" />
          Confirmed
        </div>

        <div className="flex justify-center relative z-10">
          <div
            className="rounded-full p-2 sm:p-3"
            style={quoteThemeColorStyles.primary10Bg}
          >
            <CheckCircle
              className="w-8 h-8 sm:w-12 sm:h-12"
              style={quoteThemeColorStyles.primary}
            />
          </div>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Booking Confirmed!
        </h2>
        <p className="text-base sm:text-lg text-gray-600 max-w-md mx-auto">
          Thank you for booking with us. We&apos;ll be in touch shortly with next
          steps and payment details.
        </p>
        <QuoteBookingConfirmedDepositReminder
          quote={quote}
          depositAmount={depositAmount}
          depositDueDate={depositDueDate}

        />
        <p className="text-xs sm:text-sm text-gray-500">
          If you have any questions, please don&apos;t hesitate to contact our
          support team.
        </p>
      </div>
    );
  }

  return (
    <section
      className="bg-white rounded-xl shadow-2xl border-2 p-4 sm:p-6 space-y-4 sm:space-y-6 relative overflow-hidden hover:shadow-3xl transition-all duration-300"
      style={quoteThemeColorStyles.accentBorder}
    >
      <div
        className="absolute top-0 right-0 text-white px-6 py-1.5 rounded-bl-2xl text-xs font-semibold flex items-center gap-1"
        style={quoteThemeColorStyles.primaryBg}
      >
        <Send className="w-3 h-3" />
        Next Steps
      </div>

      <div className="flex space-x-2 sm:space-x-4 border-b relative z-10">
        <button
          className={`pb-2 px-2 sm:px-4 text-sm sm:text-base transition-all duration-200 ${
            activeTab === "book"
              ? `border-b-2 font-semibold`
              : "text-gray-500 hover:text-gray-700"
          }`}
          style={{ borderColor: activeTab === "book" ? quoteThemeCssVars.primary : "" }}
          onClick={() => setActiveTab("book")}
        >
          <span className="hidden sm:inline">Continue Booking</span>
          <span className="sm:hidden">Book</span>
        </button>
        <button
          className={`pb-2 px-2 sm:px-4 text-sm sm:text-base transition-all duration-200 ${
            activeTab === "feedback"
              ? `border-b-2 font-semibold`
              : "text-gray-500 hover:text-gray-700"
          }`}
          style={{ borderColor: activeTab === "feedback" ? quoteThemeCssVars.primary : "" }}
          onClick={() => setActiveTab("feedback")}
        >
          <span className="hidden sm:inline">Request Amendments</span>
          <span className="sm:hidden">Amend</span>
        </button>
      </div>

      {status.type && (
        <div
          className={`p-3 sm:p-4 rounded-xl text-sm sm:text-base flex items-center gap-2 relative z-10 ${
            status.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {status.type === "success" ? (
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {status.message}
        </div>
      )}

      {activeTab === "book" && (
        <div className="space-y-4 sm:space-y-6 relative z-10">
          <div className="text-center">
            <h2
              className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4"
              dangerouslySetInnerHTML={{ __html: bookingTitle }}
            />
          </div>

          <QuoteBookingConfirmationOptions
            blockData={{ ...blockData, bookingBody, depositAmount, depositDueDate }}
            depositAmount={depositAmount}
            depositDueDate={depositDueDate}

            onBookingConfirmed={() => {
              setBookingConfirmed(true);
              setStatus({
                type: "success",
                message:
                  "Your booking has been confirmed! We'll be in touch shortly with next steps.",
              });
            }}
          />
        </div>
      )}

      {activeTab === "feedback" && (
        <form
          onSubmit={handleSubmitFeedback}
          className="space-y-4 sm:space-y-6 relative z-10"
        >
          <div className="text-center">
            <h2
              className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4"
              dangerouslySetInnerHTML={{
                __html: feedbackBody || "Request Changes to Your Quote",
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              What would you like us to change?
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="w-full p-2 sm:p-3 border rounded-xl min-h-[120px] sm:min-h-[150px] text-sm sm:text-base focus:outline-none focus:ring-2 transition-all duration-200"
              style={
                {
                  borderColor: quoteThemeCssVars.primary10,
                  "--tw-ring-color": quoteThemeCssVars.primary,
                } as React.CSSProperties
              }
              placeholder="Please provide details about the changes you'd like..."
              required
            />
          </div>

          <button
            type="submit"
            className="w-full py-3 sm:py-4 px-4 rounded-xl text-white relative text-sm sm:text-base font-semibold transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
            style={quoteThemeColorStyles.primaryBg}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Send className="h-4 w-4" />
                Submit Feedback
              </span>
            )}
          </button>
        </form>
      )}
    </section>
  );
}
